import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import CheckIcon from '../../assets/svg/check-icon.svg';
import InfiniteScroll from 'react-infinite-scroll-component';
import ArrowDown from '../../assets/svg/arrow-down-black.svg';
import CrossIcon from '../../assets/svg/cross.svg';
import { SelectProps } from './types';
import AttachementFile from '../attachment-file';
import useToastify from '../../hooks/use-toastify';

function Select({ showChips = false, apiHandler, value, dropdownOptions, placeholder = "Select options...", isMulti = false, isShowSearchInput = true, onChangeHandler, isDisabled, isLoading = false, clearIcon = true, fetcherDeps, isShowAttachmentFileChip, maxLimit = 1000 }: SelectProps) {
    const { errorToast } = useToastify();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [options, setOptions] = useState<any>([]);
    const [selectedOptions, setSelectedOptions] = useState<any>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [openUpwards, setOpenUpwards] = useState<boolean>(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const selectRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);

    // Custom debounce function
    const debounce = (func: any, delay: any) => {
        let timeoutId: any;
        return (...args: any) => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
        };
    };

    // Fetch options from the API with debounce
    const fetchOptions = async (term: any, currentPage: number) => {
        if (!apiHandler) {
            // Use local options when apiHandler is not provided
            if (term === null || term === undefined || term.trim() === '') {
                // If term is empty, select all options
                setOptions(dropdownOptions);
            } else {
                // Filter options based on the term
                const filteredOptions = options.filter((option: any) =>
                    option.label.toLowerCase().includes(term.toLowerCase())
                );
                setOptions(filteredOptions);
            }
        }
        else {
            if (term !== null && term !== undefined) {
                setLoading(true);
                try {
                    const options = await apiHandler(term, currentPage);
                    if (Array.isArray(options)) {
                        setOptions((prev: any) => currentPage === 1 ? options : [...prev, ...options]);
                        setHasMore(options.length > 0);
                    }
                } catch (error) {
                    console.error('Error fetching options', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setOptions([]);
            }
        }
    }

    useEffect(() => {
        if (value) {
            setSelectedOptions(value);
        }
    }, [value]);

    useEffect(() => {
        if (Array.isArray(dropdownOptions)) {
            setOptions(dropdownOptions);
        }
    }, [dropdownOptions]);

    useEffect(() => {
        if (apiHandler || isLoading) {
            fetchOptions(searchTerm, page);
        }
    }, [isLoading, page, ...(fetcherDeps || [])]);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // Close the select when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setPage(1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleCustomSelect = () => {
        setIsOpen(prev => {
            const newIsOpen = !prev;
            if (newIsOpen) {
                // Ensure input gets focus after select opens
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 0); // Using setTimeout to ensure focus is set after render

                // Calculate space to decide opening direction
                const rect = selectRef.current?.getBoundingClientRect();
                if (rect) {
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    setOpenUpwards(spaceBelow < 200 && spaceAbove > spaceBelow);
                }
            }
            return newIsOpen;
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
        // if (e?.target?.value?.length > 2)
        handleDebouncedChange(e.target.value);

    }

    const handleDebouncedChange = useCallback(  // eslint-disable-line react-hooks/exhaustive-deps
        debounce((value: string) => {
            setOptions([]);
            setPage(1);
            fetchOptions(value, 1);

        }, 1000),
        [apiHandler, options, dropdownOptions]
    );

    const loadMoreOptions = () => {
        if (!loading) {
            setPage((prev) => prev + 1);
        }
    };

    const handleOptionClick = (option: any) => {
        if (option?.haveNegativeReview) return;
        let optionsArr: any[] = [];

        if (!option) {
            // Reset selected options if user clears selection
        } else if (isMulti) {
            const isAlreadySelected = selectedOptions.some((item: any) => item.value === option.value);

            if (isAlreadySelected) {
                // Remove the option if already selected
                optionsArr = selectedOptions.filter((item: any) => item.value !== option.value);
            } else {
                if (maxLimit && selectedOptions.length >= maxLimit) {
                    errorToast(`You can select only up to ${maxLimit} values`, { position: 'top-right' });
                    return; // Prevent selection if max limit reached
                }
                optionsArr = [...selectedOptions, option];
            }
        } else {
            if (selectedOptions.length > 0 && selectedOptions?.[0].value === option.value) {
                return; // Prevent duplicate selection
            }
            optionsArr = [option];
            setIsOpen(false); // Close select after selection
        }

        onChangeHandler(optionsArr);
    };

    // const handleOptionClick = (option: any) => {
    //     let optionsArr: any[] = [];
    //     if (!option) {
    //         // setSelectedOptions(optionsArr);
    //     }
    //     else if (isMulti) {
    //         optionsArr = selectedOptions.some((item: any) => item.value === option.value)
    //             ? selectedOptions.filter((item: any) => item.value !== option.value) // Remove option if it's already selected
    //             : [...selectedOptions, option];
    //         // setSelectedOptions(optionsArr);
    //     } else {
    //         if (selectedOptions.length > 0 && selectedOptions?.[0].value === option.value) {
    //             // If the clicked option is the same as the selected one, do nothing
    //             return;
    //         }
    //         optionsArr = [option]
    //         // setSelectedOptions(optionsArr);
    //         setIsOpen(false); // Close select after selection
    //     }
    //     onChangeHandler(optionsArr);
    // };

    const handleClearSelection = (option: any) => {
        setSelectedOptions((prev: any) => prev.filter((item: any) => item !== option));
    };

    // Reset the states
    const handleReset = () => {
        setSearchTerm('');
        // setOptions([]);
        // if (e?.target?.value?.length > 2)
        handleOptionClick(null);
        handleDebouncedChange('');
        setIsOpen(false);
        setOptions([]);
        setPage(1);
        setLoading(true);
    };

    const renderSelectedItems = (selectedOptions: any) => {
        if (selectedOptions?.length === 1)
            return <span>{selectedOptions?.[0]?.label}</span>;
        else if (selectedOptions?.length > 1)
            return <span>{selectedOptions?.length + " options selected"}</span>;
        else
            return <span className='text-disabledDark font-aeonikReg'>{placeholder}</span>;
    };

    // Combine filtered options and selected options
    // const combinedOptions = Array.from(new Map([...selectedOptions, ...options].map(option => [option.value, option])).values());

    return (
        <div>
            <div className="relative" ref={selectRef}>
                <div className="flex items-center">
                    <div
                        onClick={toggleCustomSelect}
                        className={`rounded-basicRadius shadow-shadowAll font-aeonikMed text-sm p-3 bg-white w-full border border-feildBorder flex items-center justify-between mb-2 ${isDisabled ? " cursor-not-allowed !bg-lighrdark" : ""}`}>
                        {renderSelectedItems(selectedOptions)}
                        {!isDisabled && (searchTerm || selectedOptions?.length > 0) ? (
                            clearIcon ? <button
                                type="button"
                                onClick={handleReset}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                {/* Cross icon */}
                                <svg xmlns="http://www.w3.org/2000/svg" className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button> : ""
                        ) : <img src={ArrowDown} alt='' />
                        }
                    </div>

                    {isOpen && !isDisabled && (
                        <div className={`absolute w-full bg-white z-10 border shadow-shadowAll max-h-60 overflow-y-auto text-sm ${openUpwards ? 'bottom-full mb-0' : 'top-11 mt-0'}`}>
                            {isShowSearchInput &&
                                <div className='sticky w-full'>
                                    <input
                                        type="text"
                                        placeholder={"Search options..."}
                                        value={searchTerm}
                                        onChange={handleInputChange}
                                        maxLength={50}
                                        className="bg-white rounded-basicRadius outline outine-custom p-2 text-sm w-[95%] my-2 mx-auto flex focus:outline-primary sticky"
                                        ref={searchInputRef}
                                    />
                                </div>}
                            <div id="scrollableDiv" className='overflow-y-auto flex flex-col max-h-[11rem]'>
                                <InfiniteScroll
                                    dataLength={options.length}
                                    next={loadMoreOptions}
                                    hasMore={apiHandler ? hasMore : false}
                                    loader={<div className="p-2 text-center">Loading...</div>}
                                    scrollableTarget="scrollableDiv"
                                    scrollThreshold={0.8}
                                    endMessage={<p />}
                                >
                                    {
                                        options.map((option: any) => {
                                            const findSelected = selectedOptions.some(
                                                (item: any) => item.value === option.value
                                            );
                                            return (
                                                <div
                                                    key={option.value}
                                                    onClick={() => handleOptionClick(option)}
                                                    className={`p-3 font-aeonikMed flex items-center cursor-pointer hover:bg-lighterGrey ${findSelected ? 'bg-gray-200 hover:!bg-gray-200' : ''
                                                        }`}
                                                >
                                                    <div className="flex justify-between w-full">
                                                        <div className="text-sm text-darkGreyCol">{option.label}</div>
                                                        <div>
                                                            {findSelected && (
                                                                <img src={CheckIcon} alt='' className={`size-5 mr-1 group-hover:text-primary text-primary`} />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                </InfiniteScroll>
                                {options.length === 0 && !loading ? <div className="p-2 text-center text-sm text-darkGreyCol">No data found</div>
                                    : null}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showChips &&
                <div >
                    {isShowAttachmentFileChip ?
                        <div className="grid md:grid-cols-2 grid-cols-1  gap-x-2">
                            {selectedOptions.map((option: any, index: any) => {
                                return (
                                    <AttachementFile key={index} item={option} handleRemoveFile={() => {
                                        handleOptionClick(option);
                                        handleClearSelection(option);
                                    }} />
                                )
                            })}
                        </div>
                        :
                        selectedOptions.map((option: any) => {
                            return (
                                <div
                                    key={option.value}
                                    className="inline-flex items-center px-1.5 py-1.5 mr-2  mb-2 bg-transparent text-sm text-labelTextCol border border-labelTextCol rounded-basicRadius"
                                >
                                    <span>{option.label}</span>
                                    {(!isDisabled) && !option?.haveNegativeReview && <button
                                        onClick={() => {
                                            handleOptionClick(option);
                                            handleClearSelection(option);
                                        }}
                                        className="ml-3.5 text-labelTextCol"
                                    >
                                        <img src={CrossIcon} alt="" className='w-[85%]' />
                                        {/* &times; */}
                                    </button>}
                                </div>
                            )
                        })}
                </div>
            }
        </div>
    );
};

export default memo(Select);